import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { LocationService } from '../services/LocationService';

// Constants
const MAX_RADIUS = 50000; // 50km
const POLL_INTERVAL = 5 * 60 * 1000; // 기본 5분 폴링
const POLL_INTERVAL_STEPS = [5 * 60 * 1000, 15 * 60 * 1000, 30 * 60 * 1000]; // 5m -> 15m -> 30m

/**
 * 🎯 SpotContext
 * 전역 스팟 데이터 관리 — getDocs + 단계적 폴링 (비용 최적화)
 * 
 * 목적:
 * - SPOT 페이지 데이터를 전역에서 관리
 * - 탭 전환 시 불필요한 재요청 방지
 * - 비용 절감 (초기 1회 getDocs + 5/15/30분 백오프 폴링)
 * 
 * [2026-04-07] onSnapshot → getDocs + polling 전환
 * - 기존: subscribeToSpotsInRadius (onSnapshot) → 실시간이나 읽기 비용 높음
 * - 변경: getSpotsInRadius (getDocs) + polling → 읽기 비용 90% 절감
 * - 트레이드오프: 다른 유저의 스팟이 최대 60초 지연 표시 (실무적 영향 없음)
 */
const SpotContext = createContext(null);

export const useSpotContext = () => {
    const context = useContext(SpotContext);
    if (!context) {
        throw new Error('useSpotContext must be used within SpotProvider');
    }
    return context;
};

export const SpotProvider = ({ children, environment, spotTabActive = true }) => {
    // State
    const [allSpots, setAllSpots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSubscribed, setIsSubscribed] = useState(false);

    // Location state (passed from outside)
    const [userLocation, setUserLocation] = useState(null);
    const [isLocationReady, setIsLocationReady] = useState(false);

    // Refs
    const pollIntervalRef = useRef(null);
    const lastLocationRef = useRef(null);
    const lastRadiusRef = useRef(10000); // 🎯 반경 10km 원복
    const isFetchingRef = useRef(false); // 중복 fetch 방지
    const stableFetchCountRef = useRef(0);
    const pollStepRef = useRef(0);
    const lastSignatureRef = useRef('');
    const lastFetchAtRef = useRef(0);

    /**
     * 🎯 캐시 키 생성 헬퍼
     */
    const getCacheKey = useCallback(() => {
        const storedUid = sessionStorage.getItem('pi-user-id');
        const userKey = storedUid ? `_${storedUid}` : '_guest';
        return `spot_cache_v2_${environment}${userKey}`;
    }, [environment]);

    /**
     * 🎯 캐시 저장 헬퍼
     */
    const saveToCache = useCallback((location, radius, spots) => {
        try {
            const cacheData = {
                timestamp: Date.now(),
                location: location,
                radius: radius,
                spots: spots
            };
            localStorage.setItem(getCacheKey(), JSON.stringify(cacheData));
        } catch (e) {
            console.warn('[SpotContext] Failed to save cache', e);
        }
    }, [getCacheKey]);

    /**
     * 🎯 단발 스팟 조회 (getDocs)
     */
    const fetchSpots = useCallback(async (location, radius) => {
        if (isFetchingRef.current) return; // 중복 방지
        isFetchingRef.current = true;

        try {
            const spots = await LocationService.getSpotsInRadius(location, radius, false, environment);
            setAllSpots(spots);
            setError(null);
            lastFetchAtRef.current = Date.now();
            lastLocationRef.current = location;
            lastRadiusRef.current = radius;
            saveToCache(location, radius, spots);
            const signature = spots
                .map((s) => `${s.id}:${s.status || 'active'}`)
                .sort()
                .join('|');
            if (signature === lastSignatureRef.current) {
                stableFetchCountRef.current += 1;
                if (stableFetchCountRef.current >= 2 && pollStepRef.current < POLL_INTERVAL_STEPS.length - 1) {
                    pollStepRef.current += 1;
                    stableFetchCountRef.current = 0;
                    console.log(`[SpotContext] 💤 Poll backoff step=${pollStepRef.current} interval=${POLL_INTERVAL_STEPS[pollStepRef.current] / 1000}s`);
                }
            } else {
                lastSignatureRef.current = signature;
                stableFetchCountRef.current = 0;
                pollStepRef.current = 0;
            }
            console.log(`[SpotContext] ✅ Fetched ${spots.length} spots (r=${radius / 1000}km)`);
        } catch (err) {
            console.error('[SpotContext] Fetch error:', err);
            setError(err);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [environment, saveToCache]);

    /**
     * 🎯 스팟 조회 + 폴링 시작
     * 위치 또는 반경이 변경되면 호출됨
     */
    const startFetchAndPoll = useCallback((location, radius = 10000) => {
        let distKmFromLast = null;
        if (lastLocationRef.current) {
            const { distanceBetween } = require('geofire-common');
            distKmFromLast = distanceBetween([location.lat, location.lng], [lastLocationRef.current.lat, lastLocationRef.current.lng]);
        }

        const scheduleNextPoll = (delayOverride = null) => {
            const delay = delayOverride ?? POLL_INTERVAL_STEPS[pollStepRef.current] ?? POLL_INTERVAL;
            const timerId = setTimeout(async () => {
                await fetchSpots(location, radius);
                if (pollIntervalRef.current === timerId) {
                    scheduleNextPoll();
                }
            }, delay);
            pollIntervalRef.current = timerId;
        };

        // 🎯 [Cost Optimization] 500미터 이내 이동은 기존 폴링을 유지하여 읽기 폭탄 방지
        if (pollIntervalRef.current && lastRadiusRef.current === radius && distKmFromLast !== null && distKmFromLast * 1000 < 500) {
            console.log(`[SpotContext] Moved only ${(distKmFromLast * 1000).toFixed(1)}m. Keeping existing poll (Threshold: 500m)`);
            return;
        }

        // 탭 재진입 시 같은 위치의 최신 데이터를 다시 읽지 않고 남은 폴링만 재개한다.
        const currentDelay = POLL_INTERVAL_STEPS[pollStepRef.current] || POLL_INTERVAL;
        const fetchAge = Date.now() - lastFetchAtRef.current;
        const canResume = !pollIntervalRef.current
            && lastFetchAtRef.current > 0
            && lastRadiusRef.current === radius
            && distKmFromLast !== null
            && distKmFromLast * 1000 < 500
            && fetchAge < currentDelay;
        if (canResume) {
            setIsSubscribed(true);
            scheduleNextPoll(Math.max(1000, currentDelay - fetchAge));
            return;
        }

        // 기존 폴링 정리
        if (pollIntervalRef.current) {
            console.log('[SpotContext] Cleaning up previous poll interval');
            clearTimeout(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        stableFetchCountRef.current = 0;
        pollStepRef.current = 0;
        lastSignatureRef.current = '';

        // 🎯 [Cost Optimization] 캐시 확인
        const CACHE_KEY = getCacheKey();
        const CACHE_DURATION = 30 * 60 * 1000; // 30분
        const DISTANCE_THRESHOLD = 500; // 500미터 이내면 캐시 사용

        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const { timestamp, location: cachedLoc, radius: cachedRadius, spots } = JSON.parse(cached);
                const now = Date.now();

                // 1. 시간 유효성 체크
                const isTimeValid = (now - timestamp) < CACHE_DURATION;

                // 2. 거리 유효성 체크 (정확한 미터 계산)
                const { distanceBetween } = require('geofire-common');
                const distKm = distanceBetween([location.lat, location.lng], [cachedLoc.lat, cachedLoc.lng]);
                const isDistValid = (distKm * 1000) < DISTANCE_THRESHOLD;

                // 3. 반경 유효성 체크 (캐시된 반경이 요청 반경보다 크거나 같아야 함)
                const isRadiusValid = cachedRadius >= radius;

                if (isTimeValid && isDistValid && isRadiusValid) {
                    console.log(`[SpotContext] 🟢 Using Cached Spots (${spots.length} items, r=${cachedRadius / 1000}km)`);
                    console.log(`[SpotContext] Cache Age: ${((now - timestamp) / 1000).toFixed(1)}s, Distance Delta: ${(distKm * 1000).toFixed(1)}m`);

                    setAllSpots(spots);
                    setLoading(false);
                    setError(null);
                    // 캐시 사용하더라도 백그라운드 fetch + 폴링은 시작
                } else {
                    console.log('[SpotContext] 🟡 Cache Invalid - Fetching fresh data');
                    if (!isTimeValid) console.log(`[SpotContext] Reason: Time Expired`);
                    if (!isDistValid) console.log(`[SpotContext] Reason: Distance Too Far (${distKm.toFixed(2)}km)`);
                    if (!isRadiusValid) console.log(`[SpotContext] Reason: Radius Expansion (${cachedRadius} -> ${radius})`);
                }
            }
        } catch (e) {
            console.warn('[SpotContext] Cache Read Error:', e);
            localStorage.removeItem(CACHE_KEY);
        }

        console.log(`[SpotContext] 🔵 Starting getDocs fetch + ${POLL_INTERVAL / 1000}s polling (Radius: ${radius / 1000}km)`);
        setLoading(true);
        setIsSubscribed(true);
        lastLocationRef.current = location;
        lastRadiusRef.current = radius;

        // 즉시 1회 fetch
        fetchSpots(location, radius);

        // 폴링 시작 (변화 없으면 3m -> 5m -> 10m 백오프)
        scheduleNextPoll();
    }, [environment, getCacheKey, fetchSpots]);

    /**
     * 🔄 수동 새로고침
     */
    const refreshSpots = useCallback((customRadius) => {
        console.log('[SpotContext] 🔄 Manual refresh');
        const radiusToUse = customRadius || lastRadiusRef.current || 10000;

        // 캐시 삭제
        localStorage.removeItem(getCacheKey());

        if (userLocation) {
            // 기존 폴링 정리
            if (pollIntervalRef.current) {
                clearTimeout(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
            lastLocationRef.current = null;
            startFetchAndPoll(userLocation, radiusToUse);
        }
    }, [userLocation, startFetchAndPoll, getCacheKey]);

    /**
     * SPOT 탭이 아닐 때는 폴링만 중지 (위치/캐시 상태는 유지 → 재진입 시 빠른 복귀)
     * spotTabActive가 다시 true이면 동일 위치로 fetch + 폴링 재개
     */
    useEffect(() => {
        if (!spotTabActive) {
            if (pollIntervalRef.current) {
                clearTimeout(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
            return;
        }
        if (isLocationReady && userLocation) {
            const radiusToUse = lastRadiusRef.current || 10000;
            startFetchAndPoll(userLocation, radiusToUse);
        }
    }, [spotTabActive, isLocationReady, userLocation, startFetchAndPoll]);

    /**
     * 🎯 컴포넌트 unmount 시 폴링 정리
     */
    useEffect(() => {
        return () => {
            console.log('[SpotContext] 🔴 Provider unmounting - cleaning up poll interval');
            if (pollIntervalRef.current) {
                clearTimeout(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, []);

    /**
     * 🎯 스팟 업데이트 (Optimistic UI)
     */
    const updateSpot = useCallback((spotId, updates) => {
        setAllSpots(prev => prev.map(s =>
            s.id === spotId ? { ...s, ...updates } : s
        ));
    }, []);

    const value = {
        // State
        allSpots,
        loading,
        error,
        isSubscribed,
        userLocation,
        isLocationReady,

        // Actions
        setUserLocation,
        setIsLocationReady,
        refreshSpots,
        updateSpot,
    };

    return (
        <SpotContext.Provider value={value}>
            {children}
        </SpotContext.Provider>
    );
};

export default SpotContext;
